import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import AnimatedLoadingState from '../components/AnimatedLoadingState';
import ErrorState from '../components/ErrorState';
import SynastryChartSVG from '../components/SynastryChartSVG';
import ContactManager from '../components/ContactManager';
import AIInterpretationRenderer from '../components/AIInterpretationRenderer';
import { AccessBadge, UpgradePrompt } from '../components/access';
import { TIER_ORDER } from '../data/featureAccess';
import { SubscriptionTier, getRequiredTier } from '../../shared/types/access';
import { renderMarkdown } from '../utils/renderMarkdown';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import type { Contact } from '../components/ContactManager';

// ============================================================
// Типы
// ============================================================

interface SynastryResult {
  planetsA: Array<{
    name: string;
    nameRu: string;
    symbol: string;
    sign: string;
    signRu: string;
    longitude: number;
    house: number;
    retrograde: boolean;
  }>;
  planetsB: Array<{
    name: string;
    nameRu: string;
    symbol: string;
    sign: string;
    signRu: string;
    longitude: number;
    house: number;
    retrograde: boolean;
  }>;
  housesA: Array<{
    number: number;
    sign: string;
    signRu: string;
    longitude: number;
  }>;
  housesB: Array<{
    number: number;
    sign: string;
    signRu: string;
    longitude: number;
  }>;
  aspects: Array<{
    planetA: string;
    planetARu: string;
    planetB: string;
    planetBRu: string;
    type: string;
    orb: number;
    strength: number;
    longitudeA: number;
    longitudeB: number;
  }>;
  compatibilityScore: number;
  summary: string;
  ascendantLongitudeA: number;
  ascendantLongitudeB: number;
}

interface KarmicResult {
  analysis: string;
  karmicAspects: string[];
  lessons: string[];
}

interface ProfessionalResult {
  analysis: string;
  strengths: string[];
  challenges: string[];
}

interface FriendshipResult {
  analysis: string;
  compatibility: number;
  sharedInterests: string[];
}

interface SexualResult {
  analysis: string;
  passion: number;
  intimacy: string[];
}

interface SimilarityResult {
  analysis: string;
  sharedPlanets: string[];
  similarityScore: number;
}

interface RelationshipResult {
  dynamics: string;
  strengths: string[];
  challenges: string[];
  communicationStyle: string;
  emotionalBond: string;
  longTermPotential: string;
  aiInterpretation: string;
  score: number;
}

interface FamilyResult {
  familyScore: number;
  childrenPotential: string;
  homeLife: string;
  challenges: string[];
  recommendations: string[];
  aiInterpretation: string;
}

interface BusinessResult {
  businessScore: number;
  strengths: string[];
  challenges: string[];
  roles: { personA: string; personB: string };
  recommendations: string[];
  aiInterpretation: string;
}

type AnalysisType = 'synastry' | 'karmic' | 'professional' | 'friendship' | 'sexual' | 'similarity' | 'composite' | 'davison' | 'relationship' | 'family' | 'business';

const TABS: { key: AnalysisType; label: string; icon: string }[] = [
  { key: 'synastry', label: 'Синастрия', icon: '✨' },
  { key: 'karmic', label: 'Кармическая', icon: '🔮' },
  { key: 'professional', label: 'Профессиональная', icon: '💼' },
  { key: 'friendship', label: 'Дружеская', icon: '🤝' },
  { key: 'sexual', label: 'Сексуальная', icon: '🔥' },
  { key: 'similarity', label: 'Сходство', icon: '🧬' },
  { key: 'composite', label: 'Композитная карта', icon: '🔗' },
  { key: 'davison', label: 'Дэвисон', icon: '📊' },
  { key: 'relationship', label: 'Анализ отношений', icon: '💑' },
  { key: 'family', label: 'Семейная', icon: '🏠' },
  { key: 'business', label: 'Бизнес', icon: '💰' },
];

// ============================================================
// Стили
// ============================================================

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)',
  padding: '32px 20px',
  position: 'relative',
};

const containerStyle: React.CSSProperties = {
  maxWidth: '1100px',
  margin: '0 auto',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(32px, 5vw, 48px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '12px',
  textAlign: 'center' as const,
  animation: 'goldShimmer 3s ease-in-out infinite',
};

const subtitleStyle: React.CSSProperties = {
  color: '#b0b0b0',
  fontSize: '16px',
  textAlign: 'center' as const,
  marginBottom: '32px',
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  padding: '24px',
};

const goldAccentLine: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '10%',
  right: '10%',
  height: '2px',
  background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
};

const btnSecondaryStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(212, 175, 55, 0.4)',
  color: '#d4af37',
  padding: '10px 24px',
  borderRadius: '25px',
  fontSize: '14px',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

// ============================================================
// Компонент
// ============================================================

const CompatibilityPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<AnalysisType>('synastry');

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Subscription status
  const [userTier, setUserTier] = useState<SubscriptionTier>('free');
  const [loadingTier, setLoadingTier] = useState(true);
  
  // Upgrade prompt
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [pendingFeatureId, setPendingFeatureId] = useState<string | null>(null);
  const [pendingRequiredTier, setPendingRequiredTier] = useState<SubscriptionTier>('free');

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load subscription status
  useEffect(() => {
    const fetchTier = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${API_URL}/api/payment/status`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });
        const data = await res.json();
        if (data.success && data.data?.subscription) {
          setUserTier(data.data.subscription.tier || 'free');
        } else {
          setUserTier('free');
        }
      } catch {
        setUserTier('free');
      } finally {
        setLoadingTier(false);
      }
    };
    fetchTier();
  }, []);

  // Map analysis type to feature ID
  const getFeatureIdForTab = (tabKey: AnalysisType): string => {
    const map: Record<AnalysisType, string> = {
      'synastry': 'synastry',
      'karmic': 'karmic-compat',
      'professional': 'professional-compat',
      'friendship': 'friendship',
      'sexual': 'sexual',
      'similarity': 'synastry',
      'composite': 'synastry',
      'davison': 'synastry',
      'relationship': 'synastry',
      'family': 'family',
      'business': 'business-compat',
    };
    return map[tabKey] || 'synastry';
  };

  // Check access and handle tab click
  const handleTabClick = (tabKey: AnalysisType) => {
    const featureId = getFeatureIdForTab(tabKey);
    const requiredTier = getRequiredTier(featureId);
    const userLevel = TIER_ORDER[userTier as keyof typeof TIER_ORDER] ?? 0;
    const requiredLevel = TIER_ORDER[requiredTier as keyof typeof TIER_ORDER] ?? 0;
    
    if (userLevel < requiredLevel) {
      setPendingFeatureId(featureId);
      setPendingRequiredTier(requiredTier);
      setShowUpgradePrompt(true);
      return;
    }
    
    setActiveTab(tabKey);
  };

  // Результаты анализов
  const [synastry, setSynastry] = useState<SynastryResult | null>(null);
  const [karmic, setKarmic] = useState<KarmicResult | null>(null);
  const [professional, setProfessional] = useState<ProfessionalResult | null>(null);
  const [friendship, setFriendship] = useState<FriendshipResult | null>(null);
  const [sexual, setSexual] = useState<SexualResult | null>(null);
  const [similarity, setSimilarity] = useState<SimilarityResult | null>(null);
  const [relationship, setRelationship] = useState<RelationshipResult | null>(null);
  const [family, setFamily] = useState<FamilyResult | null>(null);
  const [business, setBusiness] = useState<BusinessResult | null>(null);

  const [analyzing, setAnalyzing] = useState(false);

  // Загрузка контактов
  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Contact[]>('/synastry/contacts');
      if (res.success && res.data) {
        setContacts(res.data);
      } else {
        setError(res.error || 'Не удалось загрузить контакты');
      }
    } catch (err) {
      setError('Ошибка сети');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Загрузить результаты из кэша — возвращает true если есть данные
  const loadCachedResults = async (contactId: string): Promise<boolean> => {
    setAnalyzing(true);
    clearResults();
    try {
      const res = await api.get<any>(`/synastry/results/${contactId}`);
      if (res.success && res.data) {
        const r = res.data;
        let hasData = false;
        
        // Проверяем, есть ли вообще данные в ответе
        if (!r || Object.keys(r).length === 0) {
          setAnalyzing(false);
          return false;
        }
        
        if (r.synastry?.data) {
          hasData = true;
          const d = r.synastry.data;
          const classical = d.classicalSynastry || {};

          const PLANET_SYMBOLS: Record<string, string> = {
            Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂',
            Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇',
            NorthNode: '☊', SouthNode: '☋', Chiron: '⚷', Lilith: '⚸',
            sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
            jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
            north_node: '☊', south_node: '☋', chiron: '⚷', lilith: '⚸',
          };
          const SIGN_NAMES_RU: Record<string, string> = {
            Aries: 'Овен', Taurus: 'Телец', Gemini: 'Близнецы', Cancer: 'Рак',
            Leo: 'Лев', Virgo: 'Дева', Libra: 'Весы', Scorpio: 'Скорпион',
            Sagittarius: 'Стрелец', Capricorn: 'Козерог', Aquarius: 'Водолей', Pisces: 'Рыбы',
            aries: 'Овен', taurus: 'Телец', gemini: 'Близнецы', cancer: 'Рак',
            leo: 'Лев', virgo: 'Дева', libra: 'Весы', scorpio: 'Скорпион',
            sagittarius: 'Стрелец', capricorn: 'Козерог', aquarius: 'Водолей', pisces: 'Рыбы',
          };
          const SIGN_INDEX: Record<string, number> = {
            Aries: 0, Taurus: 1, Gemini: 2, Cancer: 3, Leo: 4, Virgo: 5,
            Libra: 6, Scorpio: 7, Sagittarius: 8, Capricorn: 9, Aquarius: 10, Pisces: 11,
            aries: 0, taurus: 1, gemini: 2, cancer: 3, leo: 4, virgo: 5,
            libra: 6, scorpio: 7, sagittarius: 8, capricorn: 9, aquarius: 10, pisces: 11,
          };

          const mapPlanet = (p: any) => ({
            name: p.name,
            nameRu: p.name_ru || p.name,
            symbol: p.symbol || PLANET_SYMBOLS[p.name] || '●',
            sign: p.sign,
            signRu: p.sign_ru || SIGN_NAMES_RU[p.sign] || p.sign,
            signIndex: SIGN_INDEX[p.sign] ?? 0,
            longitude: p.longitude || 0,
            house: p.house || 0,
            retrograde: p.retrograde || false,
          });
          const mapHouse = (h: any) => ({
            number: h.number,
            sign: h.sign,
            signRu: h.sign_ru || SIGN_NAMES_RU[h.sign] || h.sign,
            longitude: h.longitude || 0,
          });

          setSynastry({
            planetsA: (classical.planetsA || []).map(mapPlanet),
            planetsB: (classical.planetsB || []).map(mapPlanet),
            housesA: (classical.housesA || []).map(mapHouse),
            housesB: (classical.housesB || []).map(mapHouse),
            aspects: (classical.planetAspects || []).map((a: any) => ({
              planetA: a.planetA, planetB: a.planB, type: a.type, orb: a.orb, strength: a.strength,
              longitudeA: a.longitudeA || 0, longitudeB: a.longitudeB || 0,
              planetARu: a.planetA, planetBRu: a.planB,
            })),
            compatibilityScore: d.scores?.overall || 0,
            summary: '',
            ascendantLongitudeA: d.compositeChart?.ascendant?.longitude || 0,
            ascendantLongitudeB: d.davisonChart?.ascendant?.longitude || 0,
          });
        }
        if (r.karmic) { setKarmic({ ...r.karmic.data, aiInterpretation: r.karmic.aiInterpretation }); hasData = true; }
        if (r.professional) { setProfessional({ ...r.professional.data, aiInterpretation: r.professional.aiInterpretation }); hasData = true; }
        if (r.friendship) { setFriendship({ ...r.friendship.data, aiInterpretation: r.friendship.aiInterpretation }); hasData = true; }
        if (r.sexual) { setSexual({ ...r.sexual.data, aiInterpretation: r.sexual.aiInterpretation }); hasData = true; }
        if (r.similarity) { setSimilarity({ ...r.similarity.data, aiInterpretation: r.similarity.aiInterpretation }); hasData = true; }
        if (r.relationship) { setRelationship({ ...r.relationship.data, aiInterpretation: r.relationship.aiInterpretation }); hasData = true; }
        if (r.family) { setFamily({ ...r.family.data, aiInterpretation: r.family.aiInterpretation }); hasData = true; }
        if (r.business) { setBusiness({ ...r.business.data, aiInterpretation: r.business.aiInterpretation }); hasData = true; }
        setAnalyzing(false);
        return hasData;
      }
    } catch (err) {
      // Failed to load cached results
    }
    setAnalyzing(false);
    return false;
  };

  // Рассчитать все анализы для выбранного контакта (автозапуск)
  const calculateAllForContact = async (contactId: string) => {
    console.log('[DEBUG] calculateAllForContact START', contactId);
    setAnalyzing(true);
    clearResults();
    setError(null);
    try {
      // Синастрия использует другой endpoint
      console.log('[DEBUG] POST /synastry/calculate');
      const synastryRes = await api.post('/synastry/calculate', { contactId }).catch((err) => {
        console.error('[DEBUG] Synastry calculate error:', err);
        return null;
      });
      console.log('[DEBUG] Synastry result:', synastryRes);

      // Остальные анализы
      const types = ['karmic', 'professional', 'friendship', 'sexual', 'similarity', 'relationship', 'family', 'business'];
      for (const type of types) {
        console.log(`[DEBUG] POST /synastry/${type}`);
        await api.post(`/synastry/${type}`, { contactId }).catch((err) => {
          console.error(`[DEBUG] ${type} calculate error:`, err);
        });
      }
      // Перезагрузить из кэша
      console.log('[DEBUG] Reloading from cache');
      await loadCachedResults(contactId);
      console.log('[DEBUG] calculateAllForContact DONE');
    } catch (err) {
      console.error('[DEBUG] calculateAllForContact FAILED:', err);
      setError('Ошибка расчёта. Попробуйте позже.');
      setAnalyzing(false);
    }
  };

  // Автозапуск расчёта при монтировании, если контакт выбран, но данных нет
  useEffect(() => {
    console.log('[DEBUG] useEffect triggered, selectedContact:', selectedContact?.id, 'analyzing:', analyzing);
    if (selectedContact && !analyzing) {
      // Проверяем кэш и запускаем расчёт если нужно
      const checkAndCalculate = async () => {
        console.log('[DEBUG] Checking cache for contact:', selectedContact.id);
        const hasResults = await loadCachedResults(selectedContact.id);
        console.log('[DEBUG] hasResults:', hasResults);
        if (!hasResults) {
          console.log('[DEBUG] No cache, starting calculation');
          await calculateAllForContact(selectedContact.id);
        }
      };
      checkAndCalculate();
    }
  }, [selectedContact]);

  // Выбрать контакт — загружаем результаты из кэша или запускаем расчёт
  const handleSelectContact = async (contact: Contact) => {
    console.log('[DEBUG] handleSelectContact:', contact.id);
    setSelectedContact(contact);
    // Сначала пробуем загрузить из кэша
    const hasResults = await loadCachedResults(contact.id);
    console.log('[DEBUG] handleSelectContact hasResults:', hasResults);
    // Если кэш пуст — запускаем автоматический расчёт
    if (!hasResults) {
      console.log('[DEBUG] handleSelectContact starting calculation');
      calculateAllForContact(contact.id);
    }
  };

  const handleDeleteContact = (contactId: string) => {
    setContacts(prev => prev.filter(c => c.id !== contactId));
    if (selectedContact?.id === contactId) {
      setSelectedContact(null);
      clearResults();
    }
  };

  const clearResults = () => {
    setSynastry(null);
    setKarmic(null);
    setProfessional(null);
    setFriendship(null);
    setSexual(null);
    setSimilarity(null);
    setRelationship(null);
    setFamily(null);
    setBusiness(null);
  };

  // Рассчитать все анализы (для кнопки)
  const calculateAll = async () => {
    if (!selectedContact) return;
    await calculateAllForContact(selectedContact.id);
  };

  // Если нет выбранного контакта — показать только контакты
  if (!selectedContact) {
    return (
      <div style={pageStyle}>
        <div style={goldAccentLine} />
        <style>{`@keyframes goldShimmer { 0% { background-position: 0% center; } 50% { background-position: 100% center; } 100% { background-position: 0% center; } }`}</style>
        <div style={containerStyle}>
          <h1 style={titleStyle}>Совместимость</h1>
          <p style={subtitleStyle}>
            Анализ совместимости через синастрию — сравнение двух натальных карт
          </p>

          {error && <ErrorState message={error} onRetry={loadContacts} />}

          <ContactManager
            contacts={contacts}
            selectedContactId={null}
            onSelectContact={handleSelectContact}
            onDeleteContact={handleDeleteContact}
            onAddContact={(c) => setContacts(prev => [...prev, c])}
            onContactsLoaded={setContacts}
          />
        </div>
      </div>
    );
  }

  // ============================================================
  // Рендер активной вкладки
  // ============================================================

  const renderTabContent = () => {
    if (analyzing) {
      return <AnimatedLoadingState />;
    }

    // Если нет результатов — показать кнопку расчёта (fallback, обычно расчёт запускается автоматически)
    const hasAnyResults = synastry || karmic || professional || friendship || sexual || similarity || relationship || family || business;
    if (!hasAnyResults) {
      return (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔮</div>
          <p style={{ color: '#b0b0b0', marginBottom: '24px', fontSize: '16px' }}>
            Результаты расчёта пока недоступны.
            <br />
            Нажмите кнопку ниже, чтобы начать анализ.
          </p>
          <button
            onClick={calculateAll}
            style={{
              background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
              border: 'none', padding: '14px 36px', borderRadius: '30px',
              color: '#0a0a1a', fontSize: '16px', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'var(--font-sans)',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,175,55,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            ✨ Рассчитать все анализы
          </button>
        </div>
      );
    }

    switch (activeTab) {
      case 'synastry':
        return renderSynastry();
      case 'karmic':
        return renderKarmic();
      case 'professional':
        return renderProfessional();
      case 'friendship':
        return renderFriendship();
      case 'sexual':
        return renderSexual();
      case 'similarity':
        return renderSimilarity();
      case 'composite':
        return renderComposite();
      case 'davison':
        return renderDavison();
      case 'relationship':
        return renderRelationship();
      case 'family':
        return renderFamily();
      case 'business':
        return renderBusiness();
      default:
        return null;
    }
  };

  const renderSynastry = () => {
    if (!synastry) {
      return (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px' }}>
          <p style={{ color: '#b0b0b0' }}>Результаты синастрии появятся после расчёта</p>
        </div>
      );
    }

    return (
      <div>
        {/* Score + Chart */}
        <div style={{ ...cardStyle, display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <SynastryChartSVG
            planetsA={synastry.planetsA}
            planetsB={synastry.planetsB}
            housesA={synastry.housesA}
            housesB={synastry.housesB}
            aspects={synastry.aspects}
            ascendantLongitudeA={synastry.ascendantLongitudeA}
            ascendantLongitudeB={synastry.ascendantLongitudeB}
            size={500}
            nameA={user?.full_name || 'A'}
            nameB={selectedContact.name || 'B'}
          />
        </div>

        {/* Score badge */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <span
            style={{
              background:
                synastry.compatibilityScore >= 70
                  ? 'rgba(40, 167, 69, 0.2)'
                  : synastry.compatibilityScore >= 40
                  ? 'rgba(255, 193, 7, 0.2)'
                  : 'rgba(220, 53, 69, 0.2)',
              color:
                synastry.compatibilityScore >= 70
                  ? '#28a745'
                  : synastry.compatibilityScore >= 40
                  ? '#ffc107'
                  : '#dc3545',
              padding: '8px 24px',
              borderRadius: '20px',
              fontSize: '18px',
              fontWeight: 600,
            }}
          >
            Совместимость: {synastry.compatibilityScore}%
          </span>
        </div>

        {/* Summary */}
        {synastry.summary && (
          <div style={cardStyle}>
            <h3 style={{ color: '#d4af37', fontSize: '18px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '12px', marginTop: 0 }}>
              Сводка
            </h3>
            <p style={{ color: '#e0e0e0', fontSize: '15px', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {synastry.summary}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderKarmic = () => (
    <AIInterpretationRenderer
      aiInterpretation={karmic?.aiInterpretation}
      title="Кармическая совместимость"
      icon="🐉"
      fallbackMessage="Кармический анализ пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderProfessional = () => (
    <AIInterpretationRenderer
      aiInterpretation={professional?.aiInterpretation}
      title="Профессиональная совместимость"
      icon="💼"
      fallbackMessage="Профессиональный анализ пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderFriendship = () => (
    <AIInterpretationRenderer
      aiInterpretation={friendship?.aiInterpretation}
      title="Дружеская совместимость"
      icon="🤝"
      fallbackMessage="Анализ дружеской совместимости пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderSexual = () => (
    <AIInterpretationRenderer
      aiInterpretation={sexual?.aiInterpretation}
      title="Сексуальная совместимость"
      icon="🔥"
      fallbackMessage="Анализ сексуальной совместимости пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderSimilarity = () => (
    <AIInterpretationRenderer
      aiInterpretation={similarity?.aiInterpretation}
      title="Анализ сходства"
      icon="🪞"
      fallbackMessage="Анализ сходства пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderRelationship = () => (
    <AIInterpretationRenderer
      aiInterpretation={relationship?.aiInterpretation}
      title="Анализ отношений"
      icon="💕"
      fallbackMessage="Анализ отношений пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderFamily = () => (
    <AIInterpretationRenderer
      aiInterpretation={family?.aiInterpretation}
      title="Семейная совместимость"
      icon="🏠"
      fallbackMessage="Семейный анализ пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const renderBusiness = () => (
    <AIInterpretationRenderer
      aiInterpretation={business?.aiInterpretation}
      title="Бизнес-совместимость"
      icon="📊"
      fallbackMessage="Бизнес-анализ пока не рассчитан"
      isAnalyzing={analyzing}
    />
  );

  const computeCompositePlanets = () => {
    if (!synastry?.planetsA.length || !synastry?.planetsB.length) return [];
    return synastry.planetsA.map((pA) => {
      const pB = synastry.planetsB.find(p => p.name === pA.name);
      if (!pB) return pA;
      const diff = Math.abs(pA.longitude - pB.longitude);
      const midLon = diff > 180
        ? ((pA.longitude + pB.longitude) / 2 + 180) % 360
        : (pA.longitude + pB.longitude) / 2;
      const signIdx = Math.floor(midLon / 30);
      const SIGNS = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
      const SIGNS_RU: Record<string,string> = {Aries:'Овен',Taurus:'Телец',Gemini:'Близнецы',Cancer:'Рак',Leo:'Лев',Virgo:'Дева',Libra:'Весы',Scorpio:'Скорпион',Sagittarius:'Стрелец',Capricorn:'Козерог',Aquarius:'Водолей',Pisces:'Рыбы'};
      const SYMBOLS: Record<string,string> = {Sun:'☉',Moon:'☽',Mercury:'☿',Venus:'♀',Mars:'♂',Jupiter:'♃',Saturn:'♄',Uranus:'♅',Neptune:'♆',Pluto:'♇',NorthNode:'☊',SouthNode:'☋',Chiron:'⚷',Lilith:'⚸'};
      return {
        ...pA,
        longitude: midLon,
        sign: SIGNS[signIdx] || 'Aries',
        signRu: SIGNS_RU[SIGNS[signIdx]] || '',
        symbol: SYMBOLS[pA.name] || '●',
      };
    });
  };

  const renderComposite = () => {
    if (!synastry) {
      return (
        <div style={cardStyle}>
          <h3 style={{ color: '#d4af37', fontSize: '18px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '12px', marginTop: 0, textAlign: 'center' }}>
            🔗 Композитная карта
          </h3>
          <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>
            {analyzing ? 'Расчёт синастрии...' : 'Данные появятся после расчёта синастрии'}
          </p>
        </div>
      );
    }

    const compositePlanets = computeCompositePlanets();
    const compositeAsc = ((synastry.ascendantLongitudeA + synastry.ascendantLongitudeB) / 2) % 360;

    return (
      <div style={cardStyle}>
        <h3 style={{ color: '#d4af37', fontSize: '18px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '16px', marginTop: 0, textAlign: 'center' }}>
          🔗 Композитная карта
        </h3>
        <p style={{ color: '#b0b0b0', fontSize: '13px', textAlign: 'center', marginBottom: '20px' }}>
          Средние точки планет двух карт — суть отношений как отдельного целого
        </p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SynastryChartSVG
            planetsA={synastry.planetsA}
            planetsB={synastry.planetsB}
            housesA={synastry.housesA}
            housesB={synastry.housesB}
            aspects={synastry.aspects}
            ascendantLongitudeA={synastry.ascendantLongitudeA}
            ascendantLongitudeB={synastry.ascendantLongitudeB}
            showComposite={true}
            planetsComposite={compositePlanets}
            ascendantLongitudeComposite={compositeAsc}
            size={500}
          />
        </div>
      </div>
    );
  };

  const renderDavison = () => (
    <div style={cardStyle}>
      <h3 style={{ color: '#d4af37', fontSize: '18px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '12px', marginTop: 0, textAlign: 'center' }}>
        📊 Дэвисон
      </h3>
      <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>
        Раздел в разработке. Скоро появится.
      </p>
    </div>
  );

  // ============================================================
  // Главный рендер: контакт выбран, табы активны
  // ============================================================

  return (
    <div style={pageStyle}>
      <div style={goldAccentLine} />
      <style>{`@keyframes goldShimmer { 0% { background-position: 0% center; } 50% { background-position: 100% center; } 100% { background-position: 0% center; } }`}</style>
      <div style={containerStyle}>
        <h1 style={titleStyle}>Совместимость</h1>
        <p style={subtitleStyle}>
          {user?.full_name || 'Вы'} & {selectedContact.name}
        </p>

        {error && <ErrorState message={error} onRetry={loadContacts} />}

        {/* Кнопка: сменить контакт */}
        <div style={{ marginBottom: '24px' }}>
          <button
            style={btnSecondaryStyle}
            onClick={() => {
              setSelectedContact(null);
              clearResults();
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            ← Все контакты
          </button>
        </div>

        {/* Табы — адаптивная сетка */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '10px',
            marginBottom: '24px',
          }}
        >
          {TABS.map(tab => {
            const featureId = getFeatureIdForTab(tab.key);
            const requiredTier = getRequiredTier(featureId);
            const userLevel = TIER_ORDER[userTier as keyof typeof TIER_ORDER] ?? 0;
            const requiredLevel = TIER_ORDER[requiredTier as keyof typeof TIER_ORDER] ?? 0;
            const hasAccess = userLevel >= requiredLevel;
            
            return (
              <div key={tab.key} style={{ position: 'relative' }}>
                <button
                  onClick={() => hasAccess && handleTabClick(tab.key)}
                  disabled={!hasAccess}
                  style={{
                    width: '100%',
                    padding: '12px 8px',
                    borderRadius: '16px',
                    border: activeTab === tab.key
                      ? '2px solid #d4af37'
                      : '1px solid rgba(212, 175, 55, 0.2)',
                    background: activeTab === tab.key
                      ? 'rgba(212, 175, 55, 0.15)'
                      : hasAccess ? 'rgba(13, 25, 48, 0.5)' : 'rgba(13, 25, 48, 0.25)',
                    color: hasAccess ? (activeTab === tab.key ? '#d4af37' : '#c0c0c0') : '#555',
                    fontSize: '13px',
                    fontWeight: activeTab === tab.key ? 600 : 400,
                    cursor: hasAccess ? 'pointer' : 'not-allowed',
                    fontFamily: 'var(--font-sans)',
                    textAlign: 'center',
                    transition: 'all 0.25s ease',
                    opacity: hasAccess ? 1 : 0.45,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  onMouseEnter={(e) => { if (hasAccess && activeTab !== tab.key) { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.08)'; e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                  onMouseLeave={(e) => { if (hasAccess && activeTab !== tab.key) { e.currentTarget.style.background = 'rgba(13, 25, 48, 0.5)'; e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                >
                  <span style={{ fontSize: '20px' }}>{tab.icon}</span>
                  <span style={{ lineHeight: 1.2 }}>{tab.label}</span>
                </button>
                {!loadingTier && (
                  <div style={{ position: 'absolute', top: '-8px', right: '-8px' }}>
                    <AccessBadge
                      featureId={featureId}
                      userTier={userTier}
                      requiredTier={requiredTier}
                      size="sm"
                      showText={false}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Содержимое активной вкладки */}
        {renderTabContent()}
      </div>
      
      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && pendingFeatureId && (
        <UpgradePrompt
          featureId={pendingFeatureId}
          userTier={userTier}
          requiredTier={pendingRequiredTier}
          onClose={() => {
            setShowUpgradePrompt(false);
            setPendingFeatureId(null);
            setPendingRequiredTier('free');
          }}
        />
      )}
    </div>
  );
};

export default CompatibilityPage;
